// share-ui.js - admin-only "Share" tool (X / SharePoint-M365 style).
//
// Captures the CURRENT view/engine/module/plugin plus its current input values,
// mints a tracked, revocable, READ-ONLY share link via /api/shares, and presents
// it with Copy-link + social share (LinkedIn, Facebook, X). The link opens the
// app in kiosk mode (see kiosk-mode.js) for any anonymous visitor.
//
// Minting is admin-only (the button only appears for admins; the backend also
// enforces [Authorize(Roles="admin")]).

import { api } from './api.js';

// ---- detect what the admin is currently looking at --------------------------
function navLabel(viewId) {
  const n = document.querySelector('.nav-item[data-view="' + cssAttr(viewId) + '"]');
  if (!n) return '';
  // Use the primary label span, not the whole node: nav items append badge spans
  // (e.g. "addon") that would otherwise concatenate into "GRMHDaddon".
  const span = n.querySelector('span:not(.addon-badge)');
  const t = span ? span.textContent : n.textContent;
  return (t || '').replace(/\s+/g, ' ').trim();
}

function currentTarget() {
  const view = document.querySelector('.view.active');
  const viewId = view ? view.id.replace(/^view-/, '') : '';

  // a plugin open in the shared plugin view
  if (viewId === 'plugin-open' && window.__openPlugin) {
    const p = window.__openPlugin;
    const titleEl = document.querySelector('#plugin-open-body h2, #plugin-open-body h3');
    return { target: 'plugin:' + p.tier + ':' + p.id, label: titleEl ? titleEl.textContent.trim() : p.id, panel: document.getElementById('plugin-open-body') };
  }
  // an HPC Lab engine: scope to the VISIBLE engine panel (#hp-{ht}) so we capture
  // and share ONLY that one engine, never every engine in the HPC Lab view.
  if (viewId === 'hpc') {
    const panel = Array.from(document.querySelectorAll('#view-hpc [id^="hp-"], #hpc-content [id^="hp-"]')).find(p => p.offsetParent !== null);
    if (panel) {
      const ht = panel.id.replace(/^hp-/, '');
      const tab = document.querySelector('.hpc-tab[data-ht="' + cssAttr(ht) + '"]');
      return { target: 'hpc:' + ht, label: (tab ? tab.textContent : ht).replace(/\s+/g, ' ').trim(), panel };
    }
  }
  // a Pro module tab (inside whichever view hosts it)
  const adv = document.querySelector('.adv-tab-btn.active');
  if (adv && adv.dataset.tab && view && view.contains(adv)) {
    return { target: 'pro:' + adv.dataset.tab, label: (adv.textContent || adv.dataset.tab).trim(), panel: document.getElementById('adv-tab-' + adv.dataset.tab) };
  }
  // a top-level view
  if (viewId) return { target: 'view:' + viewId, label: navLabel(viewId) || viewId, panel: view };
  return null;
}

// Snapshot the current input values so the kiosk re-runs the exact configuration.
function snapshot(panel) {
  const o = {};
  if (!panel) return o;
  let total = 0;
  panel.querySelectorAll('input, select, textarea').forEach(el => {
    const k = el.id || el.name;
    if (!k) return;
    if (el.type !== 'hidden' && el.offsetParent === null) return;  // skip inputs in hidden/inactive panels
    let v;
    if (el.type === 'checkbox') v = el.checked;
    else if (el.type === 'radio') { if (!el.checked) return; v = el.value; }
    else { if (el.value === '' || el.value == null) return; v = el.value; }
    // Omit oversized free-text (custom equations / pasted data) and cap the total:
    // a share is a fixed-input proof, and the visitor re-runs with the form default
    // for any omitted field. Keeps the snapshot well under the server's 8KB cap.
    if (typeof v === 'string' && v.length > 512) return;
    const cost = k.length + (typeof v === 'string' ? v.length : 5) + 6;
    if (total + cost > 6000) return;
    o[k] = v; total += cost;
  });
  return o;
}

// Capture the rendered chart canvas (PNG) for the OG card. Picks the largest 2D
// chart canvas; skips the WebGL/Babylon 3D viewport (its buffer is usually blank
// on read-back) and tainted canvases. No canvas -> the shell uses the default card.
async function captureImage(panel, token) {
  if (!panel) return;
  let data = null;
  // 1) vchart/Plotly charts render to SVG - export via Plotly.toImage at OG size.
  try {
    const plots = [...panel.querySelectorAll('.js-plotly-plot')].filter(g => g.clientWidth > 80 && g.clientHeight > 50);
    if (plots.length && window.Plotly && window.Plotly.toImage) {
      const gd = plots.sort((a, b) => (b.clientWidth * b.clientHeight) - (a.clientWidth * a.clientHeight))[0];
      data = await window.Plotly.toImage(gd, { format: 'png', width: 1200, height: 630 });
    }
  } catch (e) { data = null; }
  // 2) fallback: largest 2D chart canvas (Chart.js); skip the WebGL/Babylon 3D viewport.
  if (!data) {
    let best = null, area = 0;
    panel.querySelectorAll('canvas').forEach(c => {
      if ((c.width || 0) < 60 || (c.height || 0) < 40) return;
      try { if (c.closest('[id*="3d" i], [id*="viewport" i], .babylon, .babylonjs')) return; } catch (e) {}
      const a = c.width * c.height; if (a > area) { area = a; best = c; }
    });
    if (best) { try { data = best.toDataURL('image/png'); } catch (e) { data = null; } }   // tainted-canvas guard
  }
  if (!data || data.length < 256) return;
  await api.uploadShareImage(token, data);
}

// ---- the Share button in the topbar (admin only) ----------------------------
function ensureButton() {
  if (document.getElementById('btn-share-view')) return;
  const tr = document.querySelector('.topbar-right');
  if (!tr) return;
  const b = document.createElement('button');
  b.id = 'btn-share-view';
  b.className = 'badge badge-clickable';
  b.title = 'Share this view as a read-only link';
  b.style.cssText = 'border:1px solid var(--border);background:transparent;cursor:pointer;margin-right:8px;';
  b.innerHTML = '<i class="fas fa-share-nodes" style="margin-right:5px;color:#7c3aed"></i>Share';
  b.addEventListener('click', openModal);
  const wrap = tr.querySelector('.user-menu-wrap');
  if (wrap) tr.insertBefore(b, wrap); else tr.appendChild(b);
  injectStyles();
}

export function onAuth(user) {
  if (user && user.role === 'admin') { ensureButton(); const b = document.getElementById('btn-share-view'); if (b) b.style.display = ''; }
  else { const b = document.getElementById('btn-share-view'); if (b) b.style.display = 'none'; }
}

// ---- the modal --------------------------------------------------------------
function openModal() {
  const tgt = currentTarget();
  let m = document.getElementById('share-modal');
  if (m) m.remove();
  m = document.createElement('div');
  m.id = 'share-modal'; m.className = 'share-overlay';
  if (!tgt) {
    m.innerHTML = card('<h3>Nothing to share here</h3><p>Open a specific engine, module, or plugin first, then click Share.</p><div class="share-actions"><button class="share-btn" id="share-close">Close</button></div>');
    document.body.appendChild(m);
    wireClose(m);
    return;
  }
  const safe = /^(view|hpc|pro|plugin):[a-z0-9:_-]{1,80}$/.test(tgt.target);
  m.innerHTML = card(
    '<h3><i class="fas fa-share-nodes"></i> Share this as a read-only link</h3>'
    + '<p class="share-sub">Anyone with the link can run <b>' + esc(tgt.label) + '</b> with its current inputs, but cannot change anything. You can revoke it anytime.</p>'
    + (safe ? '' : '<p class="share-warn">This target cannot be shared (' + esc(tgt.target) + ').</p>')
    + '<label class="share-lbl">Title shown on the preview</label>'
    + '<input id="share-label" class="share-in" value="' + esc(tgt.label) + '" maxlength="120">'
    + '<label class="share-lbl">Link expires</label>'
    + '<select id="share-exp" class="share-in"><option value="0">Never (until revoked)</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select>'
    + '<div class="share-actions"><button class="share-btn-primary" id="share-gen"' + (safe ? '' : ' disabled') + '>Create link</button>'
    + '<button class="share-btn" id="share-manage">Manage links</button>'
    + '<button class="share-btn" id="share-close">Close</button></div>'
    + '<div id="share-result"></div>'
    + '<div id="share-list"></div>'
  );
  document.body.appendChild(m);
  wireClose(m);
  m.querySelector('#share-manage').addEventListener('click', () => renderList(m.querySelector('#share-list')));
  const gen = m.querySelector('#share-gen');
  if (gen) gen.addEventListener('click', async () => {
    gen.disabled = true; gen.textContent = 'Creating...';
    const label = m.querySelector('#share-label').value.trim();
    const exp = parseInt(m.querySelector('#share-exp').value, 10) || 0;
    const params = snapshot(tgt.panel);
    const res = await api.createShare(tgt.target, label, params, exp || undefined);
    // Bake the rendered chart into the OG card so the social unfurl shows the
    // actual result (biggest CTR move). Admin's browser already has the pixels.
    if (res && res.token) { try { await captureImage(tgt.panel, res.token); } catch (e) {} }
    gen.disabled = false; gen.textContent = 'Create link';
    if (res && res.url) renderResult(m.querySelector('#share-result'), res.url);
    else m.querySelector('#share-result').innerHTML = '<p class="share-warn">' + esc((res && res.error) || 'Could not create link.') + '</p>';
  });
}

function renderResult(el, url) {
  const u = encodeURIComponent(url);
  const text = encodeURIComponent('A validated result running in your browser - GDBS by VaultSync.');
  el.innerHTML =
    '<div class="share-result-box">'
    + '<div class="share-urlrow"><input class="share-in" id="share-url" readonly value="' + esc(url) + '"><button class="share-btn-primary" id="share-copy">Copy link</button></div>'
    + '<div class="share-social">'
    + '<a class="share-soc share-li" target="_blank" rel="noopener" href="https://www.linkedin.com/sharing/share-offsite/?url=' + u + '"><i class="fab fa-linkedin"></i> LinkedIn</a>'
    + '<a class="share-soc share-x" target="_blank" rel="noopener" href="https://twitter.com/intent/tweet?url=' + u + '&text=' + text + '"><i class="fab fa-x-twitter"></i> X</a>'
    + '<a class="share-soc share-fb" target="_blank" rel="noopener" href="https://www.facebook.com/sharer/sharer.php?u=' + u + '"><i class="fab fa-facebook"></i> Facebook</a>'
    + '</div></div>';
  const copy = el.querySelector('#share-copy');
  copy.addEventListener('click', () => {
    const inp = el.querySelector('#share-url');
    try { navigator.clipboard.writeText(inp.value); } catch { inp.select(); document.execCommand && document.execCommand('copy'); }
    copy.textContent = 'Copied'; setTimeout(() => copy.textContent = 'Copy link', 1500);
  });
}

async function renderList(el) {
  el.innerHTML = '<p class="share-sub">Loading...</p>';
  const res = await api.listShares();
  const shares = (res && res.shares) || [];
  if (!shares.length) { el.innerHTML = '<p class="share-sub">No shared links yet.</p>'; return; }
  el.innerHTML = '<div class="share-list-head">Your shared links</div>' + shares.map(s =>
    '<div class="share-row" data-token="' + esc(s.token) + '">'
    + '<div class="share-row-main"><b>' + esc(s.label || s.target) + '</b><span class="share-row-meta">' + esc(s.target)
    + ' · ' + (s.revoked ? 'revoked' : s.expired ? 'expired' : 'active') + ' · ' + (s.clicks || 0) + ' views · ' + (s.runs || 0) + ' runs</span></div>'
    + (s.revoked ? '' : '<button class="share-btn-sm share-revoke">Revoke</button>')
    + '</div>'
  ).join('');
  el.querySelectorAll('.share-revoke').forEach(btn => btn.addEventListener('click', async e => {
    const row = e.target.closest('.share-row'); const token = row.dataset.token;
    btn.disabled = true; btn.textContent = '...';
    const r = await api.revokeShare(token);
    if (r && (r.revoked || r.token)) renderList(el);
    else { btn.disabled = false; btn.textContent = 'Revoke'; }
  }));
}

// ---- helpers ----------------------------------------------------------------
function card(inner) { return '<div class="share-card">' + inner + '</div>'; }
function wireClose(m) {
  const close = () => m.remove();
  m.addEventListener('click', e => { if (e.target === m) close(); });
  const c = m.querySelector('#share-close'); if (c) c.addEventListener('click', close);
}
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function cssAttr(s) { return String(s).replace(/["\\]/g, '\\$&'); }

function injectStyles() {
  if (document.getElementById('share-styles')) return;
  const css = `
  .share-overlay { position:fixed; inset:0; z-index:9600; display:flex; align-items:center; justify-content:center; background:rgba(2,6,23,.7); backdrop-filter:blur(3px); }
  .share-card { background:#0f172a; border:1px solid #334155; border-radius:14px; padding:24px 26px; width:min(520px,92vw); max-height:86vh; overflow:auto; color:#e2e8f0; box-shadow:0 20px 60px rgba(0,0,0,.5); }
  .share-card h3 { margin:0 0 6px; font-size:17px; color:#f1f5f9; }
  .share-card h3 i { color:#a78bfa; margin-right:6px; }
  .share-sub { margin:0 0 14px; font-size:13px; color:#94a3b8; line-height:1.5; }
  .share-warn { color:#f59e0b; font-size:12px; }
  .share-lbl { display:block; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#64748b; margin:10px 0 4px; }
  .share-in { width:100%; box-sizing:border-box; background:#0b1220; border:1px solid #334155; border-radius:8px; color:#e2e8f0; padding:8px 10px; font-size:13px; }
  .share-actions { display:flex; gap:8px; margin-top:16px; flex-wrap:wrap; }
  .share-btn-primary { background:#7c3aed; color:#fff; padding:9px 16px; border-radius:8px; border:none; cursor:pointer; font-weight:600; font-size:13px; }
  .share-btn-primary:hover { background:#6d28d9; } .share-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
  .share-btn { background:transparent; color:#cbd5e1; padding:9px 14px; border-radius:8px; border:1px solid #334155; cursor:pointer; font-size:13px; }
  .share-btn:hover { border-color:#475569; }
  .share-btn-sm { background:transparent; color:#f87171; border:1px solid #7f1d1d; border-radius:6px; padding:4px 10px; font-size:12px; cursor:pointer; }
  .share-result-box { margin-top:16px; padding-top:14px; border-top:1px solid #1e293b; }
  .share-urlrow { display:flex; gap:8px; }
  .share-social { display:flex; gap:8px; margin-top:12px; flex-wrap:wrap; }
  .share-soc { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; text-decoration:none; color:#fff; font-size:13px; font-weight:600; }
  .share-li { background:#0a66c2; } .share-x { background:#111827; border:1px solid #334155; } .share-fb { background:#1877f2; }
  .share-soc:hover { opacity:.9; }
  .share-list-head { margin:18px 0 8px; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#64748b; }
  .share-row { display:flex; align-items:center; gap:10px; padding:8px 10px; border:1px solid #1e293b; border-radius:8px; margin-bottom:6px; }
  .share-row-main { flex:1; min-width:0; } .share-row-main b { font-size:13px; display:block; }
  .share-row-meta { font-size:11px; color:#64748b; }`;
  const s = document.createElement('style'); s.id = 'share-styles'; s.textContent = css; document.head.appendChild(s);
}
